import {
  DataGrid,
  Dialog,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs
} from '@ijoin/components';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack
} from '@mui/material';

import { FC } from 'react';

import { UploadRow } from './bulkAdvisorUpload.utils';

export const BulkAdvisorRowDialog: FC<{
  initialTab: 'messages' | 'plans';
  onClose: () => void;
  row: UploadRow;
}> = props => {
  const messages = (
    [
      ['error', props.row.errors],
      ['warning', props.row.warnings]
    ] as const
  ).flatMap(([severity, list]) =>
    list.map((message, index) => ({
      id: `${severity}-${index}`,
      message,
      severity
    }))
  );

  return (
    <Dialog
      data-testid='bulkAdvisorRow'
      fullWidth
      isOpen
      maxWidth='md'
      onClose={props.onClose}>
      <DialogTitle variant='h3'>
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'>
          {props.row.firstName} {props.row.lastName} · {props.row.username}
          <IconButton
            aria-label='Close'
            data-testid='closeBulkAdvisorRow'
            onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Tabs defaultIndex={props.initialTab === 'plans' ? 0 : 1}>
          <TabList aria-label='Advisor row details'>
            <Tab
              data-testid='bulkAdvisorRowPlansTab'
              title={`Plans (${props.row.externalPlanIds?.length ?? 0})`}
            />
            <Tab
              data-testid='bulkAdvisorRowMessagesTab'
              title={`Messages (${messages.length})`}
            />
          </TabList>
          <TabPanel index={0}>
            <DataGrid
              columns={[{ field: 'planId', flex: 1, headerName: 'Plan ID' }]}
              disableColumnMenu
              disableRowSelectionOnClick
              getRowId={row => row.planId}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } }
              }}
              pageSizeOptions={[25, 100]}
              rows={(props.row.externalPlanIds ?? []).map(planId => ({
                planId
              }))}
              slotProps={{
                noRowsOverlay: {
                  message: props.row.hasAllPlans
                    ? 'Has access to all plans'
                    : 'No plans in the file'
                },
                root: {
                  'data-component': 'dataGrid',
                  'data-testid': 'bulkAdvisorRowPlans'
                }
              }}
            />
          </TabPanel>
          <TabPanel index={1}>
            <DataGrid
              columns={[
                {
                  display: 'flex',
                  field: 'severity',
                  headerName: 'Type',
                  renderCell: params => (
                    <StatusBadge
                      data-testid={`messageType-${params.row.id}`}
                      severity={params.value}>
                      {params.value === 'error' ? 'Error' : 'Warning'}
                    </StatusBadge>
                  ),
                  width: 120
                },
                { field: 'message', flex: 1, headerName: 'Message' }
              ]}
              disableColumnMenu
              disableRowSelectionOnClick
              hideFooter
              rows={messages}
              slotProps={{
                noRowsOverlay: { message: 'No messages' },
                root: {
                  'data-component': 'dataGrid',
                  'data-testid': 'bulkAdvisorRowMessages'
                }
              }}
            />
          </TabPanel>
        </Tabs>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid='closeBulkAdvisorRowButton'
          onClick={props.onClose}
          variant='outlined'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkAdvisorRowDialog.displayName = 'BulkAdvisorRowDialog';
